// Supabase-backed service layer for Angna store
// Replaces the in-memory store.ts for all persistent operations

import { createClient } from '@/lib/supabase/client';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { ProductSize, Product, Order, OrderItem, OrderStatus } from '@/lib/store';

export type { ProductSize, Product, Order, OrderItem, OrderStatus };

// ─── DB row types (snake_case) ────────────────────────────────────────────────
interface ProductRow {
  id: string;
  name: string;
  description: string;
  story: string;
  image: string;
  image_alt: string;
  sizes: ProductSize[];
  active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface OrderRow {
  id: string;
  reference_number: string;
  villa_number: string;
  phone_number: string;
  items: OrderItem[];
  total: number;
  order_status: OrderStatus;
  created_at: string;
  updated_at: string;
}

// ─── Mappers ──────────────────────────────────────────────────────────────────
function rowToProduct(row: ProductRow): Product {
  return {
    id: row.id,
    name: row.name,
    description: row.description,
    story: row.story,
    image: row.image,
    imageAlt: row.image_alt,
    sizes: row.sizes,
    active: row.active,
    sortOrder: row.sort_order ?? 0,
  };
}

function rowToOrder(row: OrderRow): Order {
  return {
    id: row.id,
    referenceNumber: row.reference_number,
    villaNumber: row.villa_number,
    phoneNumber: row.phone_number,
    items: row.items,
    total: Number(row.total),
    status: row.order_status,
    createdAt: row.created_at,
  };
}

// ─── Products ─────────────────────────────────────────────────────────────────
export async function getActiveProducts(supabaseClient?: SupabaseClient): Promise<Product[]> {
  const supabase = supabaseClient ?? createClient();

  // Retry up to 3 times with exponential backoff to handle transient timeouts
  const MAX_RETRIES = 3;
  for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
    const { data, error } = await supabase
      .from('products')
      .select('id, name, description, story, image, image_alt, sizes, active, sort_order, created_at')
      .eq('active', true)
      .order('sort_order', { ascending: true });

    if (!error) {
      return (data as ProductRow[]).map(rowToProduct);
    }

    const isTimeout =
      error.message?.toLowerCase().includes('timeout') ||
      error.message?.toLowerCase().includes('canceling statement');

    if (isTimeout && attempt < MAX_RETRIES) {
      // Wait before retrying: 500ms, 1000ms
      await new Promise((resolve) => setTimeout(resolve, attempt * 500));
      continue;
    }

    console.error('getActiveProducts error:', error.message);
    return [];
  }

  return [];
}

export async function getAllProducts(): Promise<Product[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .order('sort_order', { ascending: true });

  if (error) {
    console.error('getAllProducts error:', error.message);
    return [];
  }
  return (data as ProductRow[]).map(rowToProduct);
}

export async function updateProduct(id: string, updates: Partial<Product>): Promise<Product | null> {
  const supabase = createClient();
  const dbUpdates: Partial<ProductRow> = {};
  if (updates.name !== undefined) dbUpdates.name = updates.name;
  if (updates.description !== undefined) dbUpdates.description = updates.description;
  if (updates.story !== undefined) dbUpdates.story = updates.story;
  if (updates.image !== undefined) dbUpdates.image = updates.image;
  if (updates.imageAlt !== undefined) dbUpdates.image_alt = updates.imageAlt;
  if (updates.sizes !== undefined) dbUpdates.sizes = updates.sizes;
  if (updates.active !== undefined) dbUpdates.active = updates.active;
  if (updates.sortOrder !== undefined) dbUpdates.sort_order = updates.sortOrder;

  const { data, error } = await supabase
    .from('products')
    .update(dbUpdates)
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('updateProduct error:', error.message);
    return null;
  }
  return rowToProduct(data as ProductRow);
}

export async function addProduct(product: Omit<Product, 'id'>): Promise<{ data: Product | null; error: string | null }> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('products')
    .insert({
      name: product.name,
      description: product.description,
      story: product.story,
      image: product.image,
      image_alt: product.imageAlt,
      sizes: product.sizes,
      active: product.active,
      sort_order: product.sortOrder ?? 0,
    })
    .select()
    .single();

  if (error) {
    console.error('addProduct error:', error.message);
    return { data: null, error: error.message };
  }
  return { data: rowToProduct(data as ProductRow), error: null };
}

export async function updateProductSizes(productId: string, sizes: ProductSize[]): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('products')
    .update({ sizes })
    .eq('id', productId);

  if (error) {
    console.error('updateProductSizes error:', error.message);
  }
}

// ─── Orders ───────────────────────────────────────────────────────────────────
export async function getOrders(): Promise<{ orders: Order[]; error: string | null }> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('getOrders error:', error.message);
    return { orders: [], error: error.message };
  }
  return { orders: (data as OrderRow[]).map(rowToOrder), error: null };
}

export async function createOrder(params: {
  villaNumber: string;
  phoneNumber: string;
  items: OrderItem[];
}): Promise<{ order: Order | null; error: string | null }> {
  const supabase = createClient();

  // ── Pre-order stock validation ──────────────────────────────────────────────
  const productIds = [...new Set(params.items.map((i) => i.productId))];
  const { data: stockProducts, error: stockError } = await supabase
    .from('products')
    .select('id, sizes')
    .in('id', productIds);

  if (stockError || !stockProducts) {
    return { order: null, error: 'Could not verify stock. Please try again.' };
  }

  for (const item of params.items) {
    const product = stockProducts.find((p) => p.id === item.productId);
    if (!product) {
      return { order: null, error: `Product not found: ${item.productName}` };
    }
    const size = (product.sizes as ProductSize[]).find((s) => s.id === item.sizeId);
    if (!size) {
      return { order: null, error: `Size not found for ${item.productName}` };
    }
    // Skip stock checks entirely for unlimited inventory sizes
    if (size.unlimitedInventory) continue;
    if (size.stock <= 0) {
      return { order: null, error: `"${item.productName} (${item.sizeLabel})" is sold out.` };
    }
    if (item.quantity > size.stock) {
      return {
        order: null,
        error: `Only ${size.stock} unit(s) of "${item.productName} (${item.sizeLabel})" available. Please reduce your quantity.`,
      };
    }
  }
  // ── End stock validation ────────────────────────────────────────────────────

  const total = params.items.reduce((sum, item) => sum + item.lineTotal, 0);

  // Generate reference number using sequence
  const { data: seqData, error: seqError } = await supabase.rpc('nextval', {
    regclass: 'public.order_seq',
  });

  let seqNum = 1;
  if (!seqError && seqData) {
    seqNum = Number(seqData);
  } else {
    // Fallback: count existing orders
    const { count } = await supabase
      .from('orders')
      .select('*', { count: 'exact', head: true });
    seqNum = (count ?? 0) + 1;
  }

  const referenceNumber = `ANGNA-${String(seqNum).padStart(4, '0')}`;

  const { data, error } = await supabase
    .from('orders')
    .insert({
      reference_number: referenceNumber,
      villa_number: params.villaNumber,
      phone_number: params.phoneNumber,
      items: params.items,
      total,
      order_status: 'New',
    })
    .select()
    .single();

  if (error) {
    console.error('createOrder error:', error.message);
    return { order: null, error: 'Failed to place order. Please try again.' };
  }

  // Deduct stock for each ordered item
  await deductStock(params.items);

  return { order: rowToOrder(data as OrderRow), error: null };
}

async function deductStock(items: OrderItem[]): Promise<void> {
  const supabase = createClient();

  // Call the atomic RPC for each item — the database does the subtraction
  // directly on the current stored value, preventing any read-then-write race.
  for (const item of items) {
    const { error } = await supabase.rpc('decrement_product_stock', {
      p_product_id: item.productId,
      p_size_id: item.sizeId,
      p_quantity: item.quantity,
    });
    if (error) {
      console.error('deductStock RPC error:', error.message);
    }
  }
}

export async function updateOrderStatus(orderId: string, status: OrderStatus): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('orders')
    .update({ order_status: status })
    .eq('id', orderId);

  if (error) {
    console.error('updateOrderStatus error:', error.message);
  }
}

export function getPendingTotal(orders: Order[]): number {
  return orders
    .filter((o) => o.status === 'New' || o.status === 'Confirmed')
    .reduce((sum, o) => sum + o.total, 0);
}

export function getTodayCount(orders: Order[]): number {
  const today = new Date().toDateString();
  return orders.filter((o) => new Date(o.createdAt).toDateString() === today).length;
}
